/**
 * Centralized Application Configuration
 * All paths, constants, and strings used throughout the application
 */

// ============================================================================
// APP INFORMATION
// ============================================================================
export const APP_CONFIG = {
  name: 'TrackAI',
  shortName: 'TrackAI',
  version: '1.0.0',
  description: 'AI-Powered Cricket Training Platform',
  supportEmail: 'cricketx@xrevstudio.com',
  defaultUserName: 'Cricket Player',
};

// ============================================================================
// FIRESTORE COLLECTION PATHS
// ============================================================================
export const FIRESTORE_PATHS = {
  // ---- Primary User Collection (SINGLE source of truth) ----
  USERS: 'Users',

  // User Document (profile fields stored directly on doc)
  getUserDoc: (userId) => `Users/${userId}`,

  // ---- Training Data (flattened — was 4 levels deep, now 2) ----
  getTrainingProfile: (userId) => `Users/${userId}/TrainingProfile/current`,
  getTrainingMatches: (userId) => `Users/${userId}/TrainingMatches`,
  getTrainingMatch: (userId, matchId) => `Users/${userId}/TrainingMatches/${matchId}`,
  getTrainingSessions: (userId) => `Users/${userId}/TrainingSessions`,
  getTrainingSession: (userId, sessionId) => `Users/${userId}/TrainingSessions/${sessionId}`,
  getCustomDrills: (userId) => `Users/${userId}/CustomDrills`,
  getCustomDrill: (userId, drillId) => `Users/${userId}/CustomDrills/${drillId}`,
  TRAINING_PROFILE: {
    BASE: 'TrainingProfile',
    CURRENT: 'current',
  },
  TRAINING_MATCHES: 'TrainingMatches',
  TRAINING_SESSIONS: 'TrainingSessions',
  CUSTOM_DRILLS: 'CustomDrills',

  // ---- Stats (shared subcollection: VR writes allTime/monthly, Web writes webTraining) ----
  getStatsAllTime: (userId) => `Users/${userId}/Stats/allTime`,
  getStatsMonthly: (userId, yearMonth) => `Users/${userId}/Stats/monthly_${yearMonth}`,
  getStatsWebTraining: (userId) => `Users/${userId}/Stats/webTraining`,
  getStatsWebTrainingMonthly: (userId, yearMonth) => `Users/${userId}/Stats/webTraining_monthly_${yearMonth}`,
  STATS: {
    BASE: 'Stats',
    ALL_TIME: 'allTime',
    WEB_TRAINING: 'webTraining',
  },

  // ---- Social (subcollections under user) ----
  getFriends: (userId) => `Users/${userId}/Friends`,
  getTeams: (userId) => `Users/${userId}/Teams`,
  FRIENDS: 'Friends',

  // ---- VR Data Paths (READ-ONLY from CricketXtreme — written by VRCricket) ----
  getVRSessions: (userId) => `Users/${userId}/Sessions`,
  getVRSessionDeliveries: (userId, sessionId) => `Users/${userId}/Sessions/${sessionId}/Deliveries`,
  VR_DATA: {
    SESSIONS: 'Sessions',
    DELIVERIES: 'Deliveries',
  },

  // ---- User Analytics (new top-level collection — written by VRCricket) ----
  USER_ANALYTICS: 'UserAnalytics',
  getUserAnalyticsVisits: (userId) => `UserAnalytics/${userId}/Visits`,
  getUserAnalyticsVisitEvents: (userId, visitId) => `UserAnalytics/${userId}/Visits/${visitId}/Events`,
  ANALYTICS_DATA: {
    VISITS: 'Visits',
    EVENTS: 'Events',
  },

  // ---- Top-Level Collections (PascalCase) ----
  SUBSCRIPTIONS: 'Subscriptions',
  FREE_BATS: 'FreeBats',
  ORDERS: 'Orders',
  EMAIL_VERIFICATIONS: 'EmailVerifications',
  FRIEND_REQUESTS: 'FriendRequests',
  TEAM_REQUESTS: 'TeamRequests',
  TEAMS: 'Teams',
  CHATS: 'Chats',
  MESSAGES: 'Messages',

  // Global chat thread ID
  GLOBAL_CHAT_THREAD_ID: 'global_dugout',

  // Chat helpers
  getChatThread: (threadId) => `Chats/${threadId}`,
  getChatMessages: (threadId) => `Chats/${threadId}/Messages`,

  // ---- @deprecated — Old paths kept during migration transition ----
  /** @deprecated Use USERS instead */
  USERS_LEGACY: 'users',
  /** @deprecated Use getUserDoc(userId) instead */
  getUserProfile: (userId) => `Users/${userId}/PlayerProfile/profile`,
  /** @deprecated Use getTrainingProfile(userId) instead */
  getTrainingProfileLegacy: (userId) => `Users/${userId}/Training/profile`,
  /** @deprecated Use getStatsWebTraining(userId) instead */
  getTrainingStats: (userId) => `Users/${userId}/Training/stats`,
  /** @deprecated Use FRIEND_REQUESTS instead */
  FRIEND_REQUESTS_LEGACY: 'friend_requests',
  /** @deprecated Use TEAM_REQUESTS instead */
  TEAM_REQUESTS_LEGACY: 'team_requests',
  /** @deprecated Use TEAMS instead */
  TEAMS_LEGACY: 'teams',
  /** @deprecated Use CHATS instead */
  CHATS_LEGACY: 'chats',
  /** @deprecated Use EMAIL_VERIFICATIONS instead */
  EMAIL_VERIFICATIONS_LEGACY: 'email_verifications',
  /** @deprecated Eliminated — use Chats/global_dugout/Messages */
  ARTIFACTS_CHAT: 'artifacts',
  /** @deprecated Eliminated — use Chats/global_dugout/Messages */
  DUGOUT_CHAT_LEGACY: 'dugout_chat',
};

// ============================================================================
// REALTIME DATABASE PATHS (presence only — Firebase recommended pattern)
// ============================================================================
export const RTDB_SEGMENTS = {
  USER_ROOT: 'DefaultDatabase',
  getUserRoot: (uid) => `DefaultDatabase/${uid}`,
  getPresence: (uid) => `DefaultDatabase/${uid}/presence`,
  getBuild: (uid) => `DefaultDatabase/${uid}/build`,

  // Server Data — Maintenance Mode (shared kill switch across all apps)
  MAINTENANCE_MODE: 'ServerData/VersionGate/maintenanceMode',
  MAINTENANCE_MESSAGE: 'ServerData/VersionGate/maintenanceMessage',
};

// ============================================================================
// SUBSCRIPTION CONSTANTS
// ============================================================================
export const SUBSCRIPTION_CONSTANTS = {
  // Stripe/Internal Plan Identifiers (used for selection)
  PLANS: {
    BASIC: 'basic',
    TRACKAI_PRO: 'trackaipro',
    TRACKAI_PRO_YEARLY: 'trackaipro_yearly',
    CRICKETX_PRO: 'cricketxpro',
    CRICKETX_PRO_YEARLY: 'cricketxpro_yearly',
  },
  // Database Storage Values (Normalized)
  DB_PLANS: {
    BASIC: 'basic',
    TRACKAI_PRO: 'trackaipro',
    CRICKETX_PRO: 'cricketxpro'
  },
  // Helper to get DB plan from any input (ID or DB name)
  getDbPlan: (inputId) => {
    if (!inputId) return 'basic';
    const input = inputId.toLowerCase();

    // If it's already a DB plan name or contains it, handle it
    if (input.includes('trackaipro')) return 'trackaipro';
    if (input.includes('cricketxpro')) return 'cricketxpro';
    if (input.includes('basic')) return 'basic';

    // Backward compatibility for old names
    const base = input.replace('_yearly', '');
    if (base === 'professional') return 'trackaipro';
    if (base === 'expert') return 'cricketxpro';
    if (base === 'semipro') return 'basic';

    return 'basic';
  },
  // Helper to get Display Name from DB plan
  getDisplayName: (dbPlan) => {
    if (dbPlan === 'trackaipro') return 'TrackAI Elite';
    if (dbPlan === 'cricketxpro') return 'CricketX VIP Pass';
    return 'Basic';
  },

  // Status Values
  STATUS: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    CANCELLED: 'cancelled',
    EXPIRED: 'expired',
  },

  // Billing Cycles
  CYCLES: {
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
  },

  // Default Plan (for new users)
  DEFAULT_PLAN: 'basic',
  DEFAULT_STATUS: 'inactive', // Free plan is inactive

  // Subscription Messages
  MESSAGES: {
    UPDATE_SUCCESS: 'Subscription updated successfully',
    UPDATE_ERROR: 'Failed to update subscription',
    SELECT_PLAN_SUCCESS: 'Plan selected successfully',
    LOGIN_REQUIRED: 'Please log in to manage your subscription',
  },

  // Plans Configuration (single source of truth — replaces stripe.js)
  PLAN_DETAILS: {
    BASIC: {
      id: 'basic',
      dbId: 'basic',
      name: 'Basic',
      price: 0,
      priceYearly: 0,
      isFree: true,
      features: [
        'Basic biomech analysis',
        'Core training scheduler',
        'Adherence tracking',
        'Standard performance reports',
      ],
    },
    TRACKAI_PRO: {
      id: 'trackaipro',
      idYearly: 'trackaipro_yearly',
      dbId: 'trackaipro',
      name: 'TrackAI Elite',
      price: 5,
      priceYearly: 50,
      priceId: process.env.REACT_APP_STRIPE_PRICE_TRACKAI_MONTHLY || 'price_1Sg2W5Kse7Vc8wKEDsuyw3kP',
      priceIdYearly: process.env.REACT_APP_STRIPE_PRICE_TRACKAI_YEARLY || 'price_1SjiQhKse7Vc8wKEJLvpLSiB',
      recommended: true,
      features: [
        'Everything in Basic',
        'Advanced motion breakdown',
        'Adaptive weekly planner',
        'Load balance & injury prevention',
        'Seamless Coach/Player sharing',
        'High-fidelity data visualization',
      ],
    },
    CRICKETX_PRO: {
      id: 'cricketxpro',
      idYearly: 'cricketxpro_yearly',
      dbId: 'cricketxpro',
      name: 'CricketX VIP Pass',
      price: 15,
      priceYearly: 150,
      priceId: process.env.REACT_APP_STRIPE_PRICE_VIP_MONTHLY || 'price_1Sg2W2Kse7Vc8wKEqBfp565O',
      priceIdYearly: process.env.REACT_APP_STRIPE_PRICE_VIP_YEARLY || 'price_1SjiRiKse7Vc8wKEzqVIj8Ev',
      features: [
        'Everything in TrackAI Elite',
        'CricketX VR Pro Access',
        'Premium biomechanics analysis',
        'Priority support',
        'Advanced analytics dashboard',
      ],
      featuresYearly: [
        'Everything in TrackAI Elite',
        'Free VR Cricket Bat included (Yearly Exclusive)',
        'CricketX VR Pro Access',
        'Premium biomechanics analysis',
        'Priority support',
      ],
    },
  },

  /**
   * Look up a plan by its database ID (e.g. 'basic', 'trackaipro', 'cricketxpro').
   * Also handles yearly variants (e.g. 'trackaipro_yearly' → 'trackaipro').
   * Returns the plan object from PLAN_DETAILS, or null if not found.
   */
  getPlanByDbId: (dbId) => {
    if (!dbId) return null;
    // Normalize: strip _yearly suffix and use getDbPlan for backward-compat mapping
    const normalized = SUBSCRIPTION_CONSTANTS.getDbPlan(dbId);
    return Object.values(SUBSCRIPTION_CONSTANTS.PLAN_DETAILS).find(p => p.dbId === normalized) || null;
  },
};

// ============================================================================
// STRIPE CONFIGURATION (public — safe to commit)
// ============================================================================
// Uses REACT_APP_STRIPE_PUBLISHABLE_KEY from .env.local (test mode) if available,
// otherwise falls back to live key (production builds).
export const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  || 'pk_live_51PXqfsKse7Vc8wKEdrm3qtiNWwe8jalXMmUAajCEF7ShtOILvavFSSgnlt8kotv0JJFNBoUnXtlK7TeMP2EF55rV00X5NWzk6q';

export const IS_STRIPE_TEST_MODE = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').startsWith('pk_test_');

// ============================================================================
// ROUTES
// ============================================================================
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  VERIFY_OTP: '/verify-otp',
  FORGOT_PASSWORD: '/forgot-password',
  SELECT_PLAN: '/select-plan',
  PAYMENT_SUCCESS: '/payment-success',
  SETTINGS: '/settings',
  PLAYER_PROFILE: '/crm',
};

// ============================================================================
// SHOPIFY CONFIGURATION
// ============================================================================
export const SHOPIFY_CONFIG = {
  SHOP_URL: 'https://cricketxtreme.myshopify.com', // Shopify store URL
  getShopLink: (userEmail) => {
    if (!userEmail) return SHOPIFY_CONFIG.SHOP_URL;
    // Use the correct format for pre-filling email in Shopify
    // Format: https://store.myshopify.com?checkout[email]=email@example.com
    return `${SHOPIFY_CONFIG.SHOP_URL}?checkout[email]=${encodeURIComponent(userEmail)}`;
  },
};

// ============================================================================
// CLOUD FUNCTIONS
// ============================================================================
export const CLOUD_FUNCTIONS = {
  BASE_URL: 'https://us-central1-vrcricket-65cd3.cloudfunctions.net',
  SEND_OTP: 'sendOTP',
  CREATE_CHECKOUT_SESSION: 'createCheckoutSession',
  VERIFY_CHECKOUT_SESSION: 'verifyCheckoutSession',
  STRIPE_WEBHOOK: 'stripeWebhook',
  SHOPIFY_WEBHOOK: 'shopifyWebhook',

  getFunctionUrl: (functionName) => {
    return `https://us-central1-vrcricket-65cd3.cloudfunctions.net/${functionName}`;
  },
};

// ============================================================================
// UI CONSTANTS
// ============================================================================
export const UI_CONSTANTS = {
  // Navigation Tabs
  TABS: {
    DASHBOARD: 'dashboard',
    BIOCAM: 'bio',
    TRAINING: 'train',
    TACTICS: 'tactics',
    DUGOUT: 'social',
    CRICKETX_PROFILE: 'crm',
  },

  // OTP
  OTP_LENGTH: 6,
  OTP_EXPIRY_MINUTES: 10,
  OTP_RESEND_COOLDOWN_SECONDS: 60,

  // Timers
  PAYMENT_SUCCESS_REDIRECT_SECONDS: 5,

  // Local Storage Keys
  STORAGE_KEYS: {
    REMEMBERED_EMAIL: 'rememberedEmail',
    LAST_CHECKIN: 'last_checkin_',
  },
};

// ============================================================================
// VALIDATION CONSTANTS
// ============================================================================
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 6,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get full Firestore path for user document (profile)
 */
export const getUserDocPath = (userId) => {
  return FIRESTORE_PATHS.getUserDoc(userId);
};

/**
 * Get full Firestore path for training profile
 */
export const getTrainingProfilePath = (userId) => {
  return FIRESTORE_PATHS.getTrainingProfile(userId);
};

/**
 * Get last check-in storage key for user
 */
export const getLastCheckInKey = (userId) => {
  return `${UI_CONSTANTS.STORAGE_KEYS.LAST_CHECKIN}${userId}`;
};

/**
 * Get current year-month string for monthly stats (e.g., "2026-02")
 */
export const getCurrentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Generate a chat thread ID from two user IDs (deterministic, sorted)
 */
export const getChatThreadId = (userId1, userId2) => {
  return [userId1, userId2].sort().join('_');
};

// ============================================================================
// VERSION GATE
// ============================================================================

/** Remote Config key this app checks for minimum version. */
export const VERSION_GATE_KEY = 'min_version_cricketxtreme';

/**
 * Compare semantic versions. Returns true if `current` is below `minimum`.
 * Strips non-numeric suffixes (e.g., "1.0.0b" → [1, 0, 0]) before comparing.
 */
export const isVersionBelow = (current, minimum) => {
  if (!current || !minimum || minimum === '0.0.0') return false;
  const normalize = (v) => v.trim().replace(/[^0-9.]/g, '').split('.').map(Number);
  const cur = normalize(current);
  const min = normalize(minimum);
  for (let i = 0; i < Math.max(cur.length, min.length); i++) {
    const c = cur[i] || 0;
    const m = min[i] || 0;
    if (c < m) return true;
    if (c > m) return false;
  }
  return false;
};

